import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { showingbookerAPI } from '../../../services/showingbookerAPI';

const OverviewContainer = styled.div`
  padding: 20px 0;
  max-width: 1200px;
  margin: 0 auto;
`;

const WelcomeSection = styled.div`
  margin-bottom: 30px;
  
  h2 {
    margin: 0 0 8px 0;
    color: #333;
    font-size: 28px;
    font-weight: 700;
  }
  
  p {
    margin: 0;
    color: #666;
    font-size: 16px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
`;

const StatCard = styled.button`
  background: white;
  border: none;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border-left: 4px solid ${props => props.color || '#007AFF'};
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
  text-align: left;
  width: 100%;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const StatIcon = styled.span`
  font-size: 24px;
`;

const StatTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  color: #666;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;
`;

const StatChange = styled.div`
  font-size: 12px;
  color: ${props => props.positive ? '#28a745' : '#666'};
  font-weight: 500;
`;

const QuickActionsSection = styled.div`
  margin-bottom: 40px;
  
  h3 {
    margin: 0 0 20px 0;
    color: #333;
    font-size: 20px;
    font-weight: 600;
  }
`;

const ActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ActionCard = styled.button`
  background: white;
  border: 2px solid #f0f0f0;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  
  &:hover {
    border-color: ${props => props.color || '#007AFF'};
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  }
`;

const ActionIcon = styled.div`
  font-size: 28px;
  margin-bottom: 12px;
`;

const ActionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const ActionSubtitle = styled.div`
  font-size: 12px;
  color: #666;
`;

const ActivitySection = styled.div`
  h3 {
    margin: 0 0 20px 0;
    color: #333;
    font-size: 20px;
    font-weight: 600;
  }
`;

const ActivityFeed = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ActivityIcon = styled.span`
  font-size: 16px;
  margin-top: 2px;
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityMessage = styled.div`
  color: #333;
  font-size: 14px;
  margin-bottom: 4px;
`;

const ActivityTime = styled.div`
  color: #666;
  font-size: 12px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;
  
  .icon {
    font-size: 48px;
    margin-bottom: 16px;
  }
  
  h3 {
    margin: 0 0 8px 0;
    color: #333;
  }
  
  p {
    margin: 0;
    font-size: 14px;
  }
`;

const Overview = () => {
  const navigate = useNavigate();
  const [dashboardStats, setDashboardStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Load dashboard stats
      const stats = await showingbookerAPI.getDashboardStats();
      setDashboardStats(stats);
      
      // Load real recent activity (no mock data)
      // TODO: Implement real recent activity API endpoint
      setRecentActivity([]);
      
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    {
      title: "Active Properties",
      value: dashboardStats?.properties || 0,
      change: dashboardStats?.properties > 0 ? "+2 this month" : "Add your first property",
      icon: "🏠",
      color: "#007AFF",
      positive: true,
      action: () => navigate('/properties')
    },
    {
      title: "Total Showings",
      value: dashboardStats?.totalBookings || 0,
      change: dashboardStats?.totalBookings > 0 ? "+3 this week" : "No bookings yet",
      icon: "📅",
      color: "#28a745",
      positive: true,
      action: () => navigate('/upcoming')
    },
    {
      title: "Hours Saved",
      value: `${dashboardStats?.hoursSaved || 0}+`,
      change: "Time you've saved",
      icon: "⏰",
      color: "#ffc107",
      positive: true,
      action: () => navigate('/stats')
    },
    {
      title: "This Month",
      value: dashboardStats?.monthlyBookings || 0,
      change: "New bookings",
      icon: "📈",
      color: "#17a2b8",
      positive: true,
      action: () => navigate('/calendar')
    }
  ];

  const quickActions = [
    {
      title: "Add Property",
      subtitle: "List a new property",
      icon: "",
      action: () => navigate('/add'),
      color: "#007AFF"
    },
    {
      title: "View Properties", 
      subtitle: "Manage all listings",
      icon: "",
      action: () => navigate('/properties'),
      color: "#28a745"
    },
    {
      title: "Calendar View",
      subtitle: "4-week schedule",
      icon: "",
      action: () => navigate('/calendar'),
      color: "#6f42c1"
    },
    {
      title: "Generate QR Code",
      subtitle: "Quick sharing",
      icon: "",
      action: () => navigate('/properties'),
      color: "#fd7e14"
    },
    {
      title: "View Reports",
      subtitle: "Analytics & insights",
      icon: "", 
      action: () => alert('Reports feature coming soon!'),
      color: "#20c997"
    },
    {
      title: "Help & Support",
      subtitle: "Get assistance",
      icon: "",
      action: () => navigate('/support'),
      color: "#e83e8c"
    }
  ];

  if (loading) {
    return (
      <OverviewContainer>
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
          Loading your dashboard...
        </div>
      </OverviewContainer>
    );
  }

  return (
    <OverviewContainer>
      <WelcomeSection>
        <h2>📊 ShowingBooker Overview</h2>
        <p>Your property showing management dashboard</p>
      </WelcomeSection>


      {/* Stats Cards */}
      <StatsGrid>
        {stats.map((stat, index) => (
          <StatCard key={index} color={stat.color} onClick={stat.action}>
            <StatHeader>
              <StatTitle>{stat.title}</StatTitle>
              <StatIcon>{stat.icon}</StatIcon>
            </StatHeader>
            <StatValue>{stat.value}</StatValue>
            <StatChange positive={stat.positive}>
              {stat.change}
            </StatChange>
          </StatCard>
        ))}
      </StatsGrid>

      {/* Quick Actions */}
      <QuickActionsSection>
        <h3>⚡ Quick Actions</h3>
        <ActionsGrid>
          {quickActions.map((action, index) => (
            <ActionCard 
              key={index} 
              color={action.color}
              onClick={action.action}
            >
              <ActionIcon>{action.icon}</ActionIcon>
              <ActionTitle>{action.title}</ActionTitle>
              <ActionSubtitle>{action.subtitle}</ActionSubtitle>
            </ActionCard>
          ))}
        </ActionsGrid>
      </QuickActionsSection>

      {/* Recent Activity */}
      <ActivitySection>
        <h3>📋 Recent Activity</h3>
        <ActivityFeed>
          {recentActivity.length > 0 ? (
            recentActivity.map((activity, index) => (
              <ActivityItem key={index}>
                <ActivityIcon>{activity.icon}</ActivityIcon>
                <ActivityContent>
                  <ActivityMessage>{activity.message}</ActivityMessage>
                  <ActivityTime>{activity.time}</ActivityTime>
                </ActivityContent>
              </ActivityItem>
            ))
          ) : (
            <EmptyState>
              <div className="icon">📝</div>
              <h3>No recent activity</h3>
              <p>Start by adding your first property to see activity here</p>
            </EmptyState>
          )}
        </ActivityFeed>
      </ActivitySection>
    </OverviewContainer>
  );
};

export default Overview;